// Shared between LpoDetail (inline print) and LpoList (direct-from-list
// print) so both stay byte-for-byte identical - the list page fetches the
// same full LPO record + company info on demand before printing.
//
// signatureImageUrl is a data: URI, not a network URL - the print popup
// (StockDocumentPrintModal) is a bare window.open("", "_blank") with the
// markup document.write()'d in, so it can't carry an Authorization header
// to re-fetch an auth-gated image itself. blobToDataUrl below converts the
// authenticated fetch's result into something the popup can render with
// no further network access, same reasoning as embedding companyInfo's
// plain text fields directly rather than having the popup fetch them.
export const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// vehicleInfo ({regNo, brand, model}) is optional - only ever passed when the
// LPO is linked to a Vehicle Maintenance Request (lpo.vehicleRequestRecordGuid),
// fetched by the caller from the existing GET /vehicles/requests/{recordGuid}
// endpoint (already returns RegNo/Brand/Model - no API/DB change needed here).
// Folded into the printed Remarks line only - the stored LPO.Remarks itself is
// never touched, so this is purely a print-time addition.
export const buildLpoPrintProps = (lpo, companyInfo, signatureImageUrl, vehicleInfo) => {
  // The reference PDF's "Amount" column is the pre-VAT line amount
  // (qty x rate) - VAT is shown separately, never folded into Amount -
  // confirmed by summing the sample's Amount column against its own Sub
  // Total. l.lineTotal (qty x rate x (1 + vat%)) would double-count VAT here.
  const printLines = lpo.lines.map((l) => ({
    qty: l.orderedQty,
    productName: l.productName,
    price: l.unitRate,
    vatAmount: l.vatAmount,
    amount: l.unitRate * l.orderedQty,
  }));

  const vehicleLine = vehicleInfo
    ? `Vehicle: ${[vehicleInfo.brand, vehicleInfo.model].filter(Boolean).join(" ")}${vehicleInfo.regNo ? ` (${vehicleInfo.regNo})` : ""}`
    : "";
  const remarks = [vehicleLine, lpo.remarks].filter(Boolean).join("\n");

  return {
    title: "LOCAL PURCHASE ORDER",
    docNo: lpo.lpoNumber,
    docDate: new Date(lpo.lpoDate).toLocaleDateString(),
    refNo: lpo.refNo || "",
    branchLabel: lpo.branchCode,
    partyLabel: "Party Name",
    partyName: lpo.supplierName,
    partyAddress: lpo.supplierAddress || "",
    partyMobile: lpo.supplierMobile || lpo.supplierTelephone || "",
    supplierEmail: lpo.supplierEmail || "",
    remarks,
    paymentType: lpo.paymentType,
    purposeTypeLabel: lpo.purposeType?.replaceAll("_", " "),
    purposeType: lpo.purposeType,
    purposeDescription: lpo.purposeDescription,
    customerName: lpo.customerName,
    showVat: true,
    vatPercent: lpo.vatRate,
    showLpoFooterBlock: true,
    lines: printLines,
    totals: { subTotal: lpo.subTotal, vatAmount: lpo.vatAmount, netAmount: lpo.netAmount },
    preparedByName: lpo.createdByName,
    approvedByName: lpo.approvedByName,
    approvedByDesignation: lpo.approvedByDesignation,
    approvedDate: lpo.approvedDate ? new Date(lpo.approvedDate).toLocaleString() : null,
    signatureImageUrl: signatureImageUrl || undefined,
    ...(companyInfo && {
      companyName: companyInfo["Company.Name"] || undefined,
      companyAddress: companyInfo["Company.Address"] || undefined,
      companyPhone: companyInfo["Company.Phone"] || undefined,
      companyEmail: companyInfo["Company.Email"] || undefined,
    }),
  };
};

// An LPO is only meaningful to hand to a supplier once it has actually been
// approved - SUBMITTED/REVISED_SUBMITTED/REJECTED/CANCELLED have no business
// being printed. PARTIALLY_RECEIVED/FULLY_RECEIVED are post-approval states
// so they stay printable too.
export const LPO_PRINTABLE_STATUSES = ["APPROVED", "PARTIALLY_RECEIVED", "FULLY_RECEIVED"];
